import { FileUploadWidget } from "@/block-system/brickz/components/ui/FileUploadWidget";
import { config } from "@/config";
import {
  OutputCollectionState,
  OutputErrorFile,
  OutputFileEntry,
  UploadcareFile,
  UploadCtxProvider,
} from "@uploadcare/react-uploader";
import { allowedFileTypesConfigToArray } from "block-system/blocks/Form/Field/Editor/EditorFields/FileUploadFields";
import { useIsFileUploadsDisabled } from "lib/context/split-context";
import { usePreviousCallback } from "lib/hooks";
import {
  usePublishedPagePollingUploadStatusQuery,
  usePublishedUploadSignatureQuery,
} from "lib/uploads/hooks";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useFormState } from "react-hook-form";
import {
  DISALLOWED_MIME_TYPES,
  FILE_EXTENSIONS_WHITELIST,
} from "server/services/uploads/FILE_UPLOAD_WHITELIST";

// Prune excess file info that are not needed for the form submission. OriginalUrl is not needed because we are using the cdnUrl, and originalUrl become stale after a few days.
type PrunedFileInfo = Omit<
  UploadcareFile,
  | "isStored"
  | "isImage"
  | "s3Url"
  | "originalFilename"
  | "s3Bucket"
  | "defaultEffects"
>;

type FileUploadProps = {
  placeholder?: string;
  value?: UploadcareFile | null;
  allowedFileTypes?: string;
  onChange: (
    value?: UploadcareFile | PrunedFileInfo | PrunedFileInfo[]
  ) => void;
  maxFileSize?: number;
  blockId: string;
  isEditing?: boolean;
  id: string;
  name: string;
  required: boolean;
  multiUploadConfig?: {
    allowedFileCount: number;
  };
  helpText?: React.ReactNode;
  publicKey?: string; // Only used for testing purposes. Cannot be used in production because the upload signature needs to be validated against the public key
};

const FileUploadComponent = forwardRef<HTMLDivElement, FileUploadProps>(
  (props, ref) => {
    const fileUploadsDisabled = useIsFileUploadsDisabled();

    const isMultiUpload = Boolean(props.multiUploadConfig);

    // Note for when removing feature flag: this component now deals with both a single PrunedFileInfo and an array of PrunedFileInfo
    const defaultValue = props.value
      ? Array.isArray(props.value)
        ? props.value
        : [props.value]
      : [];

    // ToDo when removing feature flag, we now set and expect PrunedFileInfo[]
    const [files, setFiles] = useState<UploadcareFile[]>(defaultValue);

    const maxFileCount = isMultiUpload
      ? (props?.multiUploadConfig?.allowedFileCount ?? 1)
      : 1;
    const remainingFileCount = maxFileCount - files.length;

    const uploaderRef = useRef<InstanceType<UploadCtxProvider>>(null);

    const { isSubmitSuccessful } = useFormState();

    useEffect(() => {
      if (isSubmitSuccessful && files.length > 0) {
        setFiles([]);
        if (uploaderRef.current) {
          // Reset the uploader state to avoid keeping files in memory after submission
          // If we didn't, the uplaoder will keep the state of uploaded files, and it will still be visible even after form submission
          const api = uploaderRef.current.getAPI?.();
          api.removeAllFiles();
        }
      }
    }, [files.length, isSubmitSuccessful]);

    const { data: uploadSignature, refetch: refetchUploadSignature } =
      usePublishedUploadSignatureQuery(
        {
          blockId: props.blockId,
        },
        {
          enabled: !props.isEditing,
        }
      );

    const { data: uploadStatuses, isError: isUploadStatusError } =
      usePublishedPagePollingUploadStatusQuery({
        blockId: props.blockId,
        uuids: files.filter((f) => f.uuid).map((f) => f.uuid),
      });

    usePreviousCallback(uploadStatuses, (prev, current) => {
      if (!current || !files.length) {
        return props.onChange(undefined);
      }

      const previousSuccessfulFileCount = prev?.filter(
        (status) => status?.status === "success"
      ).length;

      const currentSuccessfulFiles = current.filter(
        (status) => status?.status === "success"
      );

      const moreFilesSuccessfulThanPrevious =
        currentSuccessfulFiles.length > (previousSuccessfulFileCount ?? 0);

      if (moreFilesSuccessfulThanPrevious) {
        const currentSuccessfulFilesWithUpdatedUrls = files.reduce(
          (acc: PrunedFileInfo[], file) => {
            const status = currentSuccessfulFiles.find(
              (s) => s.uuid === file.uuid
            );
            if (status) {
              acc.push({
                uuid: file.uuid,
                name: file.name,
                size: file.size,
                imageInfo: file.imageInfo,
                videoInfo: file.videoInfo,
                contentInfo: file.contentInfo,
                mimeType: file.mimeType,
                metadata: file.metadata,
                cdnUrl: status.url,
              });
            }
            return acc;
          },
          []
        );

        props.onChange(
          isMultiUpload
            ? currentSuccessfulFilesWithUpdatedUrls
            : currentSuccessfulFilesWithUpdatedUrls[0]
        );
      }
    });

    const acceptTypes = useMemo(() => {
      const defaultAllowList = FILE_EXTENSIONS_WHITELIST.map(
        (ext) => `.${ext}`
      ).join(",");
      if (!props.allowedFileTypes) return defaultAllowList;
      const allowedFileTypes = allowedFileTypesConfigToArray(
        props.allowedFileTypes
      ).filter((ext) => ext !== "*");

      const mimeTypes = allowedFileTypes
        .filter((ext) => FILE_EXTENSIONS_WHITELIST.includes(ext))
        .map((ext) => `.${ext}`);

      return mimeTypes.length ? mimeTypes.join(",") : defaultAllowList;
    }, [props.allowedFileTypes]);

    const fileSizeValidator = useCallback(
      (fileEntry: OutputFileEntry): OutputErrorFile | undefined => {
        if (
          !props.maxFileSize ||
          fileEntry.size === null ||
          fileEntry.isSuccess
        ) {
          return;
        }

        const fileSizeFormatted = `${Math.ceil((props.maxFileSize ?? 0) / (1024 * 1024))}MB`;

        if (fileEntry.size > props.maxFileSize) {
          return {
            type: "FILE_SIZE_EXCEEDED",
            payload: { entry: fileEntry },
            message: `File exceeds max allowed size (${fileSizeFormatted})`,
          };
        }
      },
      [props.maxFileSize]
    );

    const fileExtensionValidator = useCallback(
      (fileInfo: OutputFileEntry) =>
        validateFile({
          fileEntry: fileInfo,
          allowedFileTypes: props.allowedFileTypes,
        }),
      [props.allowedFileTypes]
    );

    const onFileUploadSuccess = useCallback(
      (fileEntry: OutputFileEntry<"success">) => {
        if (!fileEntry.fileInfo) {
          return;
        }

        setFiles((prev) => {
          const exists = prev.some((f) => f.uuid === fileEntry.fileInfo?.uuid);
          if (!exists) {
            return [...prev, fileEntry.fileInfo];
          }
          return prev;
        });
      },
      []
    );

    const onRemoveFile = useCallback(
      (file: UploadcareFile) => {
        setFiles((prev) => prev.filter((f) => f.uuid !== file.uuid));

        if (!uploaderRef.current) {
          return;
        }

        try {
          const api = uploaderRef.current.getAPI?.();
          if (api) {
            // The updated library has updated types so that now there's an OutputFileEntry which holds the FileInfo
            // Since we only store the file info (not the entire OutputFileEntry) for form submission,
            // we need to search the uploader's current state to find the internalId of the "entry" by matching the file's uuid
            const collectionState =
              api.getOutputCollectionState() as OutputCollectionState;
            const fileEntry = collectionState.allEntries.find(
              (entry: OutputFileEntry) =>
                entry.uuid === file.uuid || entry.fileInfo?.uuid === file.uuid
            );

            if (fileEntry && fileEntry.internalId) {
              api.removeFileByInternalId(fileEntry.internalId);
            }
          }
        } catch (error) {
          console.error("Error removing file from uploader:", error);
        }

        void refetchUploadSignature();
      },
      [refetchUploadSignature]
    );

    return (
      <FileUploadWidget
        {...props}
        ref={ref}
        files={files}
        uploaderRef={uploaderRef}
        uploadSignature={uploadSignature}
        uploadStatuses={uploadStatuses}
        isUploadStatusError={isUploadStatusError}
        acceptTypes={acceptTypes}
        fileExtensionValidator={fileExtensionValidator}
        fileSizeValidator={fileSizeValidator}
        onFileUploadSuccess={onFileUploadSuccess}
        onRemoveFile={onRemoveFile}
        fileUploadsDisabled={fileUploadsDisabled}
        publicKey={
          props.publicKey ?? config().NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY
        }
        maxFileCount={maxFileCount}
        remainingFileCount={remainingFileCount}
        helpText={props.helpText}
        maxFileSize={props.maxFileSize}
      />
    );
  }
);

FileUploadComponent.displayName = "FileUploadComponent";

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (props, ref) => <FileUploadComponent {...props} ref={ref} />
);

FileUpload.displayName = "FileUpload";

export function validateFile({
  fileEntry,
  allowedFileTypes,
}: {
  fileEntry: OutputFileEntry;
  allowedFileTypes: FileUploadProps["allowedFileTypes"];
}): OutputErrorFile | undefined {
  if (fileEntry.isSuccess) {
    return;
  }

  const ext = fileEntry.name?.split(".").pop()?.toLowerCase();
  const isDisabledMimeType = DISALLOWED_MIME_TYPES.includes(
    fileEntry.mimeType ?? ""
  );

  const computedAllowedFileTypes = allowedFileTypes
    ? allowedFileTypesConfigToArray(allowedFileTypes).filter(
        (ext) => ext !== "*"
      )
    : FILE_EXTENSIONS_WHITELIST;

  if (!ext || !computedAllowedFileTypes.includes(ext) || isDisabledMimeType) {
    return {
      message: "File type not supported",
      payload: { entry: fileEntry },
      type: "FORBIDDEN_FILE_TYPE",
    };
  }
}
